import { createBrowserRouter, Navigate } from "react-router";
import type { RouteObject } from "react-router";
import { ConsoleLayout } from "@/shared/ui/console-layout";
import { AnalyticsRoute } from "@/routes/analytics-route";
import { DashboardRoute } from "@/routes/dashboard-route";
import { DeploymentsRoute } from "@/routes/deployment-selection-route";
import {
  GeneratedReportDownloadEntryRoute,
  GeneratedReportDownloadRoute,
} from "@/routes/generated-report-download-route";
// TODO: Re-enable these routes when ready
// import { HelpRoute } from "@/routes/help-route";
import { LiveViewRoute } from "@/routes/live-view-route";
import { LoginRoute } from "@/routes/login-route";
import { NotificationsRoute } from "@/routes/notifications-route";
import { PlaybackRoute } from "@/routes/playback-route";
import { SettingsRoute } from "@/routes/settings-route";
import { ProtectedRoute } from "@/features/auth/ui/protected-route";
import { SelectedDeploymentGuard } from "@/features/deployment-selection/ui/selected-deployment-guard";
import { PermissionGuard } from "@/features/deployment-selection/ui/permission-guard";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { getFirstAccessibleRoute } from "@/features/deployment-selection/utils/first-accessible-route";

function DeploymentDashboardRedirect() {
  const { selectedDeployment } = useSelectedDeployment();

  if (!selectedDeployment) {
    return <Navigate to="/deployments" replace />;
  }

  // Redirect to the first accessible route based on permissions
  const firstAccessibleRoute = getFirstAccessibleRoute(selectedDeployment);
  return <Navigate to={firstAccessibleRoute} replace />;
}

export const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginRoute />,
  },
  {
    path: "/deployments",
    element: (
      <ProtectedRoute>
        <DeploymentsRoute />
      </ProtectedRoute>
    ),
  },
  {
    path: "/generated-reports/download/:reportId",
    element: (
      <ProtectedRoute>
        <GeneratedReportDownloadEntryRoute />
      </ProtectedRoute>
    ),
  },
  {
    path: "/deployments/:deploymentId",
    element: (
      <ProtectedRoute>
        <SelectedDeploymentGuard />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DeploymentDashboardRedirect />,
      },
      {
        path: "generated-reports/download/:reportId",
        element: <GeneratedReportDownloadRoute />,
      },
      {
        element: <ConsoleLayout />,
        children: [
          {
            path: "dashboard",
            element: (
              <PermissionGuard requiredPermission="deployment.dashboard.read">
                <DashboardRoute />
              </PermissionGuard>
            ),
            handle: { title: "Dashboard" },
          },
          {
            path: "live",
            element: (
              <PermissionGuard requiredPermission="deployment.live.read">
                <LiveViewRoute />
              </PermissionGuard>
            ),
            handle: { title: "Live view" },
          },
          {
            path: "analytics",
            element: (
              <PermissionGuard requiredPermission="deployment.analytics.read">
                <AnalyticsRoute />
              </PermissionGuard>
            ),
            handle: { title: "Analytics" },
          },
          {
            path: "notifications",
            element: (
              <PermissionGuard requiredPermission="deployment.notifications.read">
                <NotificationsRoute />
              </PermissionGuard>
            ),
            handle: { title: "Notifications" },
          },
          {
            path: "playback",
            element: (
              <PermissionGuard requiredPermission="deployment.playback.read">
                <PlaybackRoute />
              </PermissionGuard>
            ),
            handle: { title: "Historical Playback" },
          },
          {
            path: "settings",
            element: (
              <PermissionGuard requiredPermission="deployment.settings.read">
                <SettingsRoute />
              </PermissionGuard>
            ),
            handle: { title: "Settings" },
          },
          // {
          //   path: "help",
          //   element: <HelpRoute />,
          //   handle: { title: "Help" },
          // },
          { path: "*", element: <DeploymentDashboardRedirect /> },
        ],
      },
    ],
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Navigate to="/deployments" replace />
      </ProtectedRoute>
    ),
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
